/**
 * Maps React Router path patterns to i18n keys for document metadata.
 * Patterns are matched in order from most to least specific.
 * Keys reference `meta.customer.pages.*` in the shared i18n translations.
 */
export interface PageMetaEntry {
  titleKey: string;
  descriptionKey: string;
}

export const PAGE_META_MAP: Array<{ pattern: string; meta: PageMetaEntry }> = [
  // Auth
  {
    pattern: "/login",
    meta: {
      titleKey: "meta.customer.pages.login.title",
      descriptionKey: "meta.customer.pages.login.description",
    },
  },
  {
    pattern: "/register/organization",
    meta: {
      titleKey: "meta.customer.pages.registerOrganization.title",
      descriptionKey: "meta.customer.pages.registerOrganization.description",
    },
  },
  {
    pattern: "/register",
    meta: {
      titleKey: "meta.customer.pages.register.title",
      descriptionKey: "meta.customer.pages.register.description",
    },
  },
  {
    pattern: "/verify",
    meta: {
      titleKey: "meta.customer.pages.verify.title",
      descriptionKey: "meta.customer.pages.verify.description",
    },
  },
  {
    pattern: "/invite/accept",
    meta: {
      titleKey: "meta.customer.pages.inviteAccept.title",
      descriptionKey: "meta.customer.pages.inviteAccept.description",
    },
  },
  {
    pattern: "/forgot-password",
    meta: {
      titleKey: "meta.customer.pages.forgotPassword.title",
      descriptionKey: "meta.customer.pages.forgotPassword.description",
    },
  },

  // New order wizard (match before /orders)
  {
    pattern: "/orders/new/configuration",
    meta: {
      titleKey: "meta.customer.pages.newOrderConfiguration.title",
      descriptionKey: "meta.customer.pages.newOrderConfiguration.description",
    },
  },
  {
    pattern: "/orders/new/location",
    meta: {
      titleKey: "meta.customer.pages.newOrderLocation.title",
      descriptionKey: "meta.customer.pages.newOrderLocation.description",
    },
  },
  {
    pattern: "/orders/new/details",
    meta: {
      titleKey: "meta.customer.pages.newOrderDetails.title",
      descriptionKey: "meta.customer.pages.newOrderDetails.description",
    },
  },
  {
    pattern: "/orders/new/review",
    meta: {
      titleKey: "meta.customer.pages.newOrderReview.title",
      descriptionKey: "meta.customer.pages.newOrderReview.description",
    },
  },

  // Payment return (signed redirect landing)
  {
    pattern: "/payments/return",
    meta: {
      titleKey: "meta.customer.pages.paymentReturn.title",
      descriptionKey: "meta.customer.pages.paymentReturn.description",
    },
  },

  // Order detail and checkout (match before /orders)
  {
    pattern: "/orders/:id/checkout",
    meta: {
      titleKey: "meta.customer.pages.orderCheckout.title",
      descriptionKey: "meta.customer.pages.orderCheckout.description",
    },
  },
  {
    pattern: "/orders/:id",
    meta: {
      titleKey: "meta.customer.pages.orderDetail.title",
      descriptionKey: "meta.customer.pages.orderDetail.description",
    },
  },
  {
    pattern: "/orders",
    meta: {
      titleKey: "meta.customer.pages.orders.title",
      descriptionKey: "meta.customer.pages.orders.description",
    },
  },

  // Requests & vendors
  {
    pattern: "/requests/:requestId/vendors/:offerId",
    meta: {
      titleKey: "meta.customer.pages.vendorOffer.title",
      descriptionKey: "meta.customer.pages.vendorOffer.description",
    },
  },
  {
    pattern: "/requests/:id/vendors",
    meta: {
      titleKey: "meta.customer.pages.vendors.title",
      descriptionKey: "meta.customer.pages.vendors.description",
    },
  },
  {
    pattern: "/requests/:id",
    meta: {
      titleKey: "meta.customer.pages.biddingRoom.title",
      descriptionKey: "meta.customer.pages.biddingRoom.description",
    },
  },
  {
    pattern: "/requests",
    meta: {
      titleKey: "meta.customer.pages.requests.title",
      descriptionKey: "meta.customer.pages.requests.description",
    },
  },

  // Services (match detail before list)
  {
    pattern: "/services/:id",
    meta: {
      titleKey: "meta.customer.pages.serviceDetail.title",
      descriptionKey: "meta.customer.pages.serviceDetail.description",
    },
  },
  {
    pattern: "/services",
    meta: {
      titleKey: "meta.customer.pages.services.title",
      descriptionKey: "meta.customer.pages.services.description",
    },
  },

  // Profile
  {
    pattern: "/profile/account",
    meta: {
      titleKey: "meta.customer.pages.profileAccount.title",
      descriptionKey: "meta.customer.pages.profileAccount.description",
    },
  },
  {
    pattern: "/profile/locations",
    meta: {
      titleKey: "meta.customer.pages.profileLocations.title",
      descriptionKey: "meta.customer.pages.profileLocations.description",
    },
  },
  {
    pattern: "/profile/wallet",
    meta: {
      titleKey: "meta.customer.pages.profileWallet.title",
      descriptionKey: "meta.customer.pages.profileWallet.description",
    },
  },
  {
    pattern: "/profile/team",
    meta: {
      titleKey: "meta.customer.pages.profileTeam.title",
      descriptionKey: "meta.customer.pages.profileTeam.description",
    },
  },
  {
    pattern: "/profile/security",
    meta: {
      titleKey: "meta.customer.pages.profileSecurity.title",
      descriptionKey: "meta.customer.pages.profileSecurity.description",
    },
  },

  // Other portal pages
  {
    pattern: "/contact",
    meta: {
      titleKey: "meta.customer.pages.contact.title",
      descriptionKey: "meta.customer.pages.contact.description",
    },
  },
  {
    pattern: "/not-allowed",
    meta: {
      titleKey: "meta.customer.pages.notAllowed.title",
      descriptionKey: "meta.customer.pages.notAllowed.description",
    },
  },
  {
    pattern: "/",
    meta: {
      titleKey: "meta.customer.pages.dashboard.title",
      descriptionKey: "meta.customer.pages.dashboard.description",
    },
  },
];

export const SITE_NAME_KEY = "meta.customer.siteName";
export const DEFAULT_DESCRIPTION_KEY = "meta.customer.defaultDescription";
